package org.example.project.enginekalender

import kotlinx.serialization.encodeToString
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.contentOrNull
import org.example.project.enginechat.canSeeKontaktProfil
import org.example.project.enginechat.chatIdentityUserId
import org.example.project.enginechat.userCompanyLabel
import org.example.project.enginechat.userDisplayName
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.NodeScope
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.utils.All

private val terminJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun apiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private fun triggerKalenderRefresh(userId: String = "") {
    val userPart = userId.trim().ifBlank { "~" }
    GlobalManager.markJsonChanged("kalender", "user/$userPart/termine/termin.json")
}

suspend fun loadKalenderBookings(art: String, userId: String): List<All> {
    if (userId.isBlank()) return emptyList()
    val currentFirmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
        .ifBlank { GlobalManager.currentFirmenId.trim() }
    if (art.uppercase() != NodeScope.SYS && currentFirmenId.isNotBlank()) {
        return loadScopedKalenderBookings(currentFirmenId, userId)
    }
    val raw = runCatching { LumaticaApiManager.call("kalender/user/${apiPart(userId)}") }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { terminJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

suspend fun loadScopedKalenderBookings(firmenId: String, userId: String): List<All> {
    if (firmenId.isBlank() || userId.isBlank()) return emptyList()
    val raw = runCatching { LumaticaApiManager.call("kalender/scoped-user/${apiPart(firmenId)}/${apiPart(userId)}") }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { terminJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

suspend fun loadServiceKalenderBookings(): List<All> {
    val raw = runCatching { LumaticaApiManager.call("kalender/service-all") }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { terminJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

suspend fun searchServiceKalenderBookings(filters: Map<String, String>): List<All> {
    val raw = runCatching { loadWithTabularFilters("kalender/service-all", filters) }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { terminJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

suspend fun loadKalenderContacts(userId: String): List<All> {
    if (userId.isBlank()) return emptyList()
    val currentUser = LoginService.getUser()
    val currentFirmenId = currentUser?.firmenId.orEmpty().trim()
    if (currentFirmenId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/users/${apiPart(currentFirmenId)}")
    }.getOrDefault("")
    val contacts = runCatching { terminJson.decodeFromString<List<All>>(raw) }
        .getOrDefault(emptyList())
        .asSequence()
        .filter { it.id.isNotBlank() && it.firmenId.trim() == currentFirmenId }
        .filter { canSeeKontaktProfil(currentFirmenId, it) }
        .toList()
    return contacts
        .map { contact ->
            val rawUserId = chatIdentityUserId(contact.id)
            contact.copy(
                id = rawUserId,
                key = rawUserId,
                label = listOf(userDisplayName(contact), userCompanyLabel(contact))
                    .filter { it.isNotBlank() }
                    .joinToString(" | ")
            )
        }
        .filter { it.id.isNotBlank() && it.id != userId }
        .distinctBy { "${it.firmenId.trim()}#${it.id}" }
        .sortedBy { it.label.lowercase() }
}

suspend fun loadServiceBookingLocations(customerFirmenId: String, providerFirmenId: String): List<All> {
    if (customerFirmenId.isBlank() || providerFirmenId.isBlank()) return listOf(All(key = "online", label = "Online", ort = "Online"))
    val raw = runCatching { LumaticaApiManager.call("kalender/service-locations/${apiPart(customerFirmenId)}/${apiPart(providerFirmenId)}") }.getOrDefault("")
    if (raw.isBlank()) return listOf(All(key = "online", label = "Online", ort = "Online"))
    return runCatching { terminJson.decodeFromString<List<All>>(raw) }.getOrDefault(listOf(All(key = "online", label = "Online", ort = "Online")))
}

suspend fun loadKalenderSlotSettings(art: String, userId: String): Triple<String, String, Set<String>> {
    val defaultDays = setOf("mo", "di", "mi", "do", "fr", "sa", "so")
    if (userId.isBlank()) return Triple("", "", defaultDays)
    val raw = runCatching {
        LumaticaApiManager.call("kalender/settings/${apiPart(userId)}")
    }.getOrDefault("").trim()
    val settings = runCatching {
        terminJson.decodeFromString(All.AllNames.serializer(), raw).slots
    }.getOrDefault("").trim()
    val parts = raw.split("|")
        .takeIf { settings.isBlank() }
        ?: settings.split("|")
    val from = parts.getOrNull(0).orEmpty().takeIf { parseHourMinute(it) != null } ?: ""
    val to = parts.getOrNull(1).orEmpty().takeIf { parseHourMinute(it) != null } ?: ""
    val days = parts.getOrNull(2)
        ?.split(",")
        ?.map(::normalizedWeekdayLabel)
        ?.filter { it in defaultDays }
        ?.toSet()
        ?.takeIf { it.isNotEmpty() }
        ?: defaultDays
    return if ((parseHourMinute(from) ?: 0) < (parseHourMinute(to) ?: 0)) {
        Triple(from, to, days)
    } else {
        Triple("", "", days)
    }
}

suspend fun loadScopedKalenderSlotSettings(firmenId: String, userId: String): Triple<String, String, Set<String>> {
    val defaultDays = setOf("mo", "di", "mi", "do", "fr", "sa", "so")
    if (firmenId.isBlank() || userId.isBlank()) return Triple("", "", defaultDays)
    val raw = runCatching {
        LumaticaApiManager.call("kalender/scoped-settings/${apiPart(firmenId)}/${apiPart(userId)}")
    }.getOrDefault("").trim()
    val settings = runCatching {
        terminJson.decodeFromString(All.AllNames.serializer(), raw).slots
    }.getOrDefault("").trim()
    val parts = raw.split("|")
        .takeIf { settings.isBlank() }
        ?: settings.split("|")
    val from = parts.getOrNull(0).orEmpty().takeIf { parseHourMinute(it) != null } ?: ""
    val to = parts.getOrNull(1).orEmpty().takeIf { parseHourMinute(it) != null } ?: ""
    val days = parts.getOrNull(2)
        ?.split(",")
        ?.map(::normalizedWeekdayLabel)
        ?.filter { it in defaultDays }
        ?.toSet()
        ?.takeIf { it.isNotEmpty() }
        ?: defaultDays
    return if ((parseHourMinute(from) ?: 0) < (parseHourMinute(to) ?: 0)) {
        Triple(from, to, days)
    } else {
        Triple("", "", days)
    }
}

suspend fun saveKalenderSlotSettings(art: String, userId: String, from: String, to: String, weekdays: Set<String>) {
    if (userId.isBlank()) return
    val orderedDays = listOf("mo", "di", "mi", "do", "fr", "sa", "so").filter { it in weekdays }
    val settingValue = "$from|$to|${orderedDays.joinToString(",")}"
    LumaticaApiManager.call("kalender/settings/save/${apiPart(userId)}/${apiPart(settingValue)}")
    triggerKalenderRefresh(userId)
}

suspend fun saveScopedKalenderSlotSettings(firmenId: String, userId: String, from: String, to: String, weekdays: Set<String>) {
    if (firmenId.isBlank() || userId.isBlank()) return
    val orderedDays = listOf("mo", "di", "mi", "do", "fr", "sa", "so").filter { it in weekdays }
    val settingValue = "$from|$to|${orderedDays.joinToString(",")}"
    LumaticaApiManager.call("kalender/scoped-settings/save/${apiPart(firmenId)}/${apiPart(userId)}/${apiPart(settingValue)}")
    triggerKalenderRefresh(userId)
}

suspend fun persistKalenderBooking(
    art: String,
    booking: All
): List<All> {
    val raw = runCatching {
        if (booking.akquiseProspectId.isNotBlank()) {
            LumaticaApiManager.postJson("kalender/akquise-save-json", terminJson.encodeToString(All.AllNames.serializer(), booking))
        } else LumaticaApiManager.call(
            listOf(
                "kalender",
                "save",
                booking.id.ifBlank { "neu" },
                booking.kontakt,
                booking.erstelltVon,
                booking.datum,
                booking.uhrzeitVon,
                booking.uhrzeitBis,
                booking.bezeichnung.ifBlank { booking.art },
                booking.ort,
                booking.empfaenger,
                booking.ansprechpartner,
                booking.meetings,
                booking.snapChatId,
                booking.teilnehmerUserIds,
                booking.teilnehmerAnzeige,
                booking.beschreibung.orEmpty()
            ).joinToString("/") { apiPart(it) }
        )
    }.getOrDefault("")
    if (booking.akquiseProspectId.isNotBlank()) {
        val failure = runCatching { terminJson.parseToJsonElement(raw).jsonObject["error"]?.jsonPrimitive?.contentOrNull }.getOrNull()
        if (failure != null) error(if (failure == "zeitfenster_blockiert")
            "Das Zeitfenster ist inzwischen belegt. Bitte ein anderes Zeitfenster ausw\u00e4hlen."
            else "Kalenderbuchung oder Zuordnung zum Prospect konnte nicht gespeichert werden.")
    }
    if (raw.isBlank()) return emptyList()
    val saved = runCatching { terminJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    if (saved.isNotEmpty()) triggerKalenderRefresh(booking.kontakt)
    return saved
}

suspend fun persistServiceKalenderBooking(
    providerFirmenId: String,
    providerUserId: String,
    booking: All
): List<All> {
    val currentUser = LoginService.getUser()
    val customerFirmenId = booking.kundeFirmenId
        .ifBlank { currentUser?.firmenId.orEmpty().ifBlank { GlobalManager.currentFirmenId.trim() } }
    val customerUserId = booking.kundeUserId
        .ifBlank { currentUser?.id.orEmpty() }
    val ortTyp = booking.ortTyp
    val raw = runCatching {
        LumaticaApiManager.call(
            listOf(
                "kalender",
                "service-save",
                booking.id.ifBlank { "neu" },
                providerUserId,
                customerUserId,
                booking.datum,
                booking.uhrzeitVon,
                booking.uhrzeitBis,
                booking.bezeichnung.ifBlank { booking.art },
                booking.ort,
                "",
                "",
                "",
                "",
                providerFirmenId,
                providerUserId,
                customerFirmenId,
                customerUserId,
                ortTyp,
                booking.terminArt,
                booking.zustand.toString(),
                booking.zusatzTeilnehmer,
                booking.teilnehmerAnzeige,
                booking.beschreibung.orEmpty()
            ).joinToString("/") { apiPart(it) }
        )
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    val saved = runCatching { terminJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    if (saved.isNotEmpty()) triggerKalenderRefresh(providerUserId)
    return saved
}

suspend fun deleteKalenderBooking(
    art: String,
    kalenderUserId: String,
    booking: All
) {
    if (kalenderUserId.isBlank()) return
    LumaticaApiManager.call(
        listOf(
            "kalender",
            "delete",
            kalenderUserId,
            booking.id,
            booking.datum,
            booking.uhrzeitVon,
            booking.uhrzeitBis,
            booking.erstelltVon
        ).joinToString("/") { apiPart(it) }
    )
    triggerKalenderRefresh(kalenderUserId)
}
